import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Input, Form, Descriptions, Typography, Tag, message } from 'antd';
import { useTranslation } from 'react-i18next';

import returnOrderService from '@services/operator/returnOrderService';

const { Text } = Typography;

// Compact shape of the planned items we render — same fields the verification
// page's SearchResult.items carries from
// `return_receipts_controller#render_search_results` (fulfillment branch).
export interface PlannedItemPreview {
  product?: { sku?: string | null; name?: string | null } | null;
  quantity?: number | null;
}

export interface FulfillmentRef {
  id: string;
  number?: string | null;
  name?: string | null;
}

export interface CreateReturnOrderModalProps {
  open: boolean;
  fulfillment: FulfillmentRef | null;
  /** Items preview — limited to the verification page's filtered subset. */
  items: PlannedItemPreview[];
  /**
   * Operator-facing default name. Page composes it from the fulfillment so the
   * suggestion is meaningful at glance ("FF-123-RETURN-..."); operator may edit
   * before confirming. Server still falls back to its own auto-name if blank.
   */
  suggestedName?: string;
  /**
   * Fires with the newly-created RO ref `{ id, name, status }`. The page is
   * expected to splice this into the search result and continue the normal
   * loadResult flow.
   */
  onCreated: (returnOrder: { id: string; name: string; status: string }) => void;
  onCancel: () => void;
}

const MAX_ITEMS_INLINE = 6;

/**
 * Asks the operator to confirm "create a new ReturnOrder for this fulfillment?"
 * after scanning a fulfillment that has no existing RO.
 *
 * Calls POST /return_orders/create_from_fulfillment on confirm. The endpoint
 * is idempotent on `(fulfillment_id, status: [pending, partial_received])` —
 * if another operator just raced ahead, BE returns that existing RO instead
 * of double-creating.
 */
const CreateReturnOrderModal: React.FC<CreateReturnOrderModalProps> = ({
  open,
  fulfillment,
  items,
  suggestedName,
  onCreated,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  // Reset the editable name to the latest suggestion every time the modal opens
  // so a previous session's typing doesn't leak across scans.
  useEffect(() => {
    if (open) {
      setName(suggestedName || '');
      setSubmitting(false);
    }
  }, [open, suggestedName]);

  const totalItems = items.length;
  const totalQuantity = useMemo(
    () => items.reduce((sum, it) => sum + (it.quantity || 0), 0),
    [items],
  );

  const handleConfirm = async (): Promise<void> => {
    if (!fulfillment?.id) return;
    setSubmitting(true);
    try {
      const res = await returnOrderService.createFromFulfillment({
        fulfillmentId: fulfillment.id,
        name: name.trim() || null,
      });
      if (!res.success) {
        const code = res.message;
        const msg = code
          ? (t(`common.error.${code}`, { ...(res.interpolations || {}), defaultValue: code }) as string)
          : (t('common.error.operation_failed') as string);
        message.error(msg);
        return;
      }
      onCreated(res.data);
    } catch (err) {
      console.error('[CreateReturnOrderModal] createFromFulfillment failed:', err);
      message.error(t('common.error.operation_failed') as string);
    } finally {
      setSubmitting(false);
    }
  };

  const inlineItems = items.slice(0, MAX_ITEMS_INLINE);
  const overflow = totalItems - inlineItems.length;

  return (
    <Modal
      open={open}
      title={t('operator.return_verification.create_ro_modal.title', {
        defaultValue: '建立退貨單',
      })}
      onOk={handleConfirm}
      onCancel={onCancel}
      okText={t('operator.return_verification.create_ro_modal.confirm', {
        defaultValue: '建立並開始驗收',
      })}
      cancelText={t('common.button.cancel', { defaultValue: '取消' })}
      okButtonProps={{ loading: submitting, disabled: !fulfillment?.id }}
      cancelButtonProps={{ disabled: submitting }}
      closable={!submitting}
      maskClosable={!submitting}
      width={560}
    >
      <Text>
        {t('operator.return_verification.create_ro_modal.hint', {
          defaultValue:
            '此出貨單尚未對應任何退貨單，建立後會以下方品項做為退貨預期清單。',
        })}
      </Text>

      <Form layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item
          label={t('operator.return_verification.create_ro_modal.name_label', {
            defaultValue: '退貨單編號',
          })}
          help={t('operator.return_verification.create_ro_modal.name_help', {
            defaultValue: '可自訂，留空時系統會自動產生編號。',
          })}
        >
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={suggestedName}
            disabled={submitting}
            maxLength={120}
            autoFocus
          />
        </Form.Item>
      </Form>

      <Descriptions size="small" column={1} bordered style={{ marginBottom: 12 }}>
        <Descriptions.Item
          label={t('operator.return_verification.create_ro_modal.fulfillment_label', {
            defaultValue: '出貨單',
          })}
        >
          {fulfillment?.name || fulfillment?.number || '-'}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('operator.return_verification.create_ro_modal.items_count_label', {
            defaultValue: '預期品項',
          })}
        >
          {t('operator.return_verification.create_ro_modal.items_count_value', {
            count: totalItems,
            quantity: totalQuantity,
            defaultValue: `${totalItems} 種商品 / 共 ${totalQuantity} 件`,
          })}
        </Descriptions.Item>
      </Descriptions>

      {inlineItems.length > 0 && (
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('operator.return_verification.create_ro_modal.items_preview_label', {
              defaultValue: '商品預覽',
            })}
          </Text>
          <ul style={{ marginTop: 4, marginBottom: 0, paddingLeft: 18 }}>
            {inlineItems.map((it, idx) => (
              <li key={idx}>
                <Text strong>{it.product?.sku || '-'}</Text>
                <Text style={{ marginLeft: 8 }}>{it.product?.name || '-'}</Text>
                <Tag style={{ marginLeft: 8 }}>
                  {t('operator.return_verification.create_ro_modal.qty_tag', {
                    quantity: it.quantity || 0,
                    defaultValue: `預期 ${it.quantity || 0}`,
                  })}
                </Tag>
              </li>
            ))}
            {overflow > 0 && (
              <li>
                <Text type="secondary">
                  {t('operator.return_verification.create_ro_modal.items_overflow', {
                    count: overflow,
                    defaultValue: `…等 ${overflow} 項`,
                  })}
                </Text>
              </li>
            )}
          </ul>
        </div>
      )}
    </Modal>
  );
};

export default CreateReturnOrderModal;
